// src/components/lettings/bits.tsx — small shared pieces for the lettings screens (Task L).
import type { ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import type { Readiness } from '../../lib/lettings';

/** A coloured status chip (prototype fl-chip). */
export function Chip({ label, colour }: { label: string; colour: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11.5px] font-medium" style={{ background: `${colour}1f`, color: colour }}>
      <i className="h-1.5 w-1.5 rounded-full" style={{ background: colour }} />{label}
    </span>
  );
}

/** A filter tile: the count is a button that filters the table (prototype stat row). */
export function FilterTile({ label, value, colour, active, onClick }: { label: string; value: ReactNode; colour?: string; active?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} className={`surface min-w-0 p-4 text-left transition ${active ? 'ring-2 ring-bronze-400' : 'hover:ring-1 hover:ring-ink-200'}`}>
      <div className="text-[22px] font-bold leading-none" style={colour ? { color: colour } : undefined}>{value}</div>
      <div className="mt-1.5 truncate text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-400">{label}</div>
    </button>
  );
}

/** The portals' readiness list (prototype "Before it can go live"). */
export function ReadinessList({ readiness, compact }: { readiness: Readiness; compact?: boolean }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[12.5px] font-semibold ${readiness.done ? 'text-emerald-700' : 'text-amber-700'}`}>{readiness.ok} of {readiness.total} ready</span>
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-ink-100"><div className={`h-full ${readiness.done ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${(readiness.ok / readiness.total) * 100}%` }} /></div>
      </div>
      <ul className={`mt-3 grid gap-1.5 ${compact ? '' : 'sm:grid-cols-2'}`}>
        {readiness.items.map((i) => (
          <li key={i.key} className={`flex items-start gap-2 text-[12.5px] ${i.ok ? 'text-ink-500' : 'font-medium text-ink-800'}`}>
            {i.ok ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />}{i.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Section = ({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) => (
  <section className="mt-6 first:mt-0">
    <div className="mb-3 flex items-center gap-3">
      <h4 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-500">{title}</h4>
      <span className="h-px flex-1 bg-ink-100" />{action}
    </div>
    {children}
  </section>
);
